import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Lock, AlertCircle, CheckCircle2, Pencil, X } from 'lucide-react';

/**
 * AuthorisedValueGate - Blocking modal to confirm Already Authorised Value
 * 
 * This modal blocks access to the Pricing Builder until the FOH advisor
 * consciously confirms the value of work already authorised by the customer.
 * 
 * The system must NEVER silently default to £0 - it must be explicitly confirmed.
 */

const QUICK_TAGS = [
  { id: 'diagnostic', label: 'Diagnostic' },
  { id: 'service', label: 'Service' },
  { id: 'mot', label: 'MOT' },
  { id: 'aircon', label: 'Air Conditioning Service' },
  { id: 'other', label: 'Other' },
];

const AuthorisedValueGate = ({
  isOpen,
  onConfirm,
  onClose,
  currentValue = null,
  isChangeMode = false,
}) => {
  const [inputValue, setInputValue] = useState(currentValue !== null ? currentValue.toString() : '');
  const [selectedTags, setSelectedTags] = useState([]);
  const [mode, setMode] = useState('enter'); // 'enter' or 'zero'

  const handleTagToggle = (tagId) => {
    setSelectedTags(prev => 
      prev.includes(tagId) 
        ? prev.filter(t => t !== tagId)
        : [...prev, tagId]
    );
  };

  const handleConfirmValue = () => {
    const value = parseFloat(inputValue) || 0;
    onConfirm({
      value: value,
      tags: selectedTags,
      confirmed_zero: false,
    });
  };

  const handleConfirmZero = () => {
    onConfirm({
      value: 0,
      tags: [],
      confirmed_zero: true,
    });
  };

  const handleClose = () => {
    if (isChangeMode && onClose) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open && isChangeMode) handleClose(); }}>
      <DialogContent 
        className="sm:max-w-[500px] bg-white"
        onPointerDownOutside={(e) => { if (!isChangeMode) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (!isChangeMode) e.preventDefault(); else handleClose(); }}
      >
        {/* Close Button - Only shown in change mode */}
        {isChangeMode && onClose && (
          <button
            onClick={handleClose}
            className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground"
            data-testid="authorised-gate-close-btn"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>
        )}
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Lock className="h-5 w-5 text-amber-600" />
            {isChangeMode ? 'Change Already Authorised Value' : 'Confirm Already Authorised Value'}
          </DialogTitle>
          <DialogDescription className="text-slate-600 text-sm mt-2">
            {isChangeMode 
              ? 'Update the value of work already agreed with the customer.'
              : 'Before building this estimate, confirm the value of work already agreed with the customer.'
            }
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4 space-y-6">
          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex gap-3">
              <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">What should be included?</p>
                <p className="text-blue-700">
                  This may include diagnostics, servicing, MOT related work, air conditioning service 
                  or any other work authorised prior to inspection.
                </p>
                <p className="text-blue-700 mt-2 font-medium">
                  If nothing has yet been authorised, confirm £0.00.
                </p>
              </div>
            </div>
          </div>

          {/* Option 1: Enter Value */}
          <div className={`border-2 rounded-lg p-4 transition-all ${mode === 'enter' ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'}`}>
            <button
              className="w-full text-left"
              onClick={() => setMode('enter')}
            >
              <div className="flex items-center gap-2 mb-3">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${mode === 'enter' ? 'border-emerald-500 bg-emerald-500' : 'border-slate-300'}`}>
                  {mode === 'enter' && <CheckCircle2 className="h-3 w-3 text-white" />}
                </div>
                <span className="font-semibold text-slate-800">Enter Authorised Value</span>
              </div>
            </button>

            {mode === 'enter' && (
              <div className="space-y-4 pl-7">
                {/* Amount Input */}
                <div className="flex items-center gap-2">
                  <span className="text-lg font-bold text-slate-700">£</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder="0.00"
                    className="w-40 text-lg font-semibold"
                    autoFocus
                    data-testid="authorised-value-input"
                  />
                </div>

                {/* Quick Tags */}
                <div>
                  <p className="text-xs text-slate-500 mb-2">Optional: What does this include?</p>
                  <div className="flex flex-wrap gap-2">
                    {QUICK_TAGS.map(tag => (
                      <Badge
                        key={tag.id}
                        variant={selectedTags.includes(tag.id) ? 'default' : 'outline'}
                        className={`cursor-pointer transition-all ${
                          selectedTags.includes(tag.id)
                            ? 'bg-emerald-600 hover:bg-emerald-700'
                            : 'hover:bg-slate-100'
                        }`}
                        onClick={() => handleTagToggle(tag.id)}
                        data-testid={`tag-${tag.id}`}
                      >
                        {tag.label}
                      </Badge>
                    ))}
                  </div>
                </div>

                {/* Confirm Button */}
                <Button
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  onClick={handleConfirmValue}
                  disabled={inputValue === '' || parseFloat(inputValue) < 0}
                  data-testid="confirm-value-btn"
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Confirm £{inputValue ? parseFloat(inputValue).toFixed(2) : '0.00'} Authorised
                </Button>
              </div>
            )}
          </div>

          {/* Option 2: Nothing Authorised */}
          <div className={`border-2 rounded-lg p-4 transition-all ${mode === 'zero' ? 'border-slate-500 bg-slate-50' : 'border-slate-200 hover:border-slate-300'}`}>
            <button
              className="w-full text-left"
              onClick={() => setMode('zero')}
            >
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${mode === 'zero' ? 'border-slate-500 bg-slate-500' : 'border-slate-300'}`}>
                  {mode === 'zero' && <CheckCircle2 className="h-3 w-3 text-white" />}
                </div>
                <span className="font-semibold text-slate-800">Confirm Nothing Authorised Yet</span>
              </div>
              <p className="text-sm text-slate-500 pl-7">
                No work has been agreed with the customer prior to this inspection.
              </p>
            </button>

            {mode === 'zero' && (
              <div className="mt-4 pl-7">
                <Button
                  className="w-full bg-slate-600 hover:bg-slate-700"
                  onClick={handleConfirmZero}
                  data-testid="confirm-zero-btn"
                >
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                  Confirm £0.00 - Nothing Authorised
                </Button>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

/**
 * AuthorisedValueDisplay - Locked display after confirmation
 */
export const AuthorisedValueDisplay = ({
  value,
  tags = [],
  confirmedAt,
  confirmedBy,
  onChangeClick,
  compact = false,
}) => {
  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <Lock className="h-3 w-3 text-emerald-600" />
        <span className="text-xs text-slate-600">Authorised:</span>
        <span className="font-semibold text-emerald-700 text-sm">£{value.toFixed(2)}</span>
        {tags.length > 0 && (
          <span className="text-xs text-emerald-600">({tags.length} tags)</span>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="text-slate-500 hover:text-slate-700 h-6 px-2"
          onClick={onChangeClick}
          data-testid="change-authorised-value-btn"
        >
          <Pencil className="h-3 w-3" />
        </Button>
      </div>
    );
  }
  
  return (
    <div className="flex items-center gap-3">
      <div className="flex items-center gap-2">
        <Lock className="h-4 w-4 text-emerald-600" />
        <span className="font-semibold text-slate-700 whitespace-nowrap">Already Authorised Value:</span>
      </div>
      <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-1.5">
        <span className="font-bold text-emerald-700 text-lg">
          £{value.toFixed(2)}
        </span>
        {tags.length > 0 && (
          <div className="flex gap-1 ml-2">
            {tags.map(tag => (
              <Badge key={tag} variant="secondary" className="text-xs bg-emerald-100 text-emerald-700">
                {QUICK_TAGS.find(t => t.id === tag)?.label || tag}
              </Badge>
            ))}
          </div>
        )}
      </div>
      <Button
        variant="ghost"
        size="sm"
        className="text-slate-500 hover:text-slate-700"
        onClick={onChangeClick}
        data-testid="change-authorised-value-btn"
      >
        <Pencil className="h-4 w-4 mr-1" />
        Change
      </Button>
    </div>
  );
};

export default AuthorisedValueGate;
